import { createFileRoute } from '@tanstack/react-router';
import { Home } from '@osd/icons/home';
import { LayoutGrid } from '@osd/icons/layout-grid';
import { Users } from '@osd/icons/users';
import { Settings } from '@osd/icons/settings';
import { Sidebar, SidebarLink, SidebarSection, type NavSection } from '@osd/ui/nav';
import { Stack } from '@osd/ui/stack';
import { Table } from '@osd/ui/table';
import { Text } from '@osd/ui/text';
import { SidebarLink as RouterSidebarLink } from '../components/AppShell/SidebarLink';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/sidebar — live docs for the Sidebar primitive set.
 *
 * Three parts, all exported from `@osd/ui/nav`:
 *
 *   - <Sidebar>            — outer <nav> wrapper; renders one
 *                            SidebarSection per entry in `sections`.
 *                            Supports icon-rail collapsed mode.
 *   - <SidebarSection>     — collapsible <details> group;
 *                            persists open/closed to localStorage
 *   - <SidebarLink>        — single <a href> link with the active
 *                            class
 *
 * The design system stays router-agnostic — SidebarLink renders
 * a plain <a>. Consumers that need client-side routing wrap a
 * router <Link> with the same BEM classes (see the existing
 * apps/web SidebarLink for the TanStack Router version).
 */
export const Route = createFileRoute('/ui/_layout/sidebar')({
  component: SidebarPage,
});

const SAMPLE_SECTIONS: ReadonlyArray<NavSection> = [
  {
    id: 'main',
    label: 'Main',
    icon: <Home />,
    children: [
      { to: '/', label: 'Home', icon: <Home /> },
      { to: '/users', label: 'Users', icon: <Users /> },
      { to: '/about', label: 'About' },
    ],
  },
  {
    id: 'design',
    label: 'Design System',
    icon: <LayoutGrid />,
    children: [{ to: '/ui', label: 'Components', icon: <LayoutGrid /> }],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: <Settings />,
    adminOnly: true,
    children: [
      { to: '/admin/approvals', label: 'Approvals' },
      { to: '/admin/audit', label: 'Audit log' },
      { to: '/admin/invites', label: 'Invites' },
    ],
  },
];

function SidebarPage() {
  return (
    <Stack gap={6}>
      <Section
        title="Sidebar"
        hint="Primary nav surface — composes SidebarSection + SidebarLink. Renderer is router-agnostic."
      >
        <div
          style={{
            border: '1px solid var(--osd-color-border)',
            borderRadius: 'var(--osd-radius-md)',
            maxWidth: '20rem',
            overflow: 'hidden',
          }}
        >
          <Sidebar sections={SAMPLE_SECTIONS} ariaLabel="Docs example" />
        </div>
      </Section>

      <Section
        title="SidebarSection alone"
        hint="Collapsible <details> group; persists open state to localStorage"
      >
        <Stack gap={3}>
          {SAMPLE_SECTIONS[0] !== undefined ? (
            <SidebarSection section={SAMPLE_SECTIONS[0]} />
          ) : null}
        </Stack>
      </Section>

      <Section title="SidebarLink alone" hint="Single <a href> with the active class">
        <Stack gap={2}>
          <SidebarLink to="/">Home</SidebarLink>
          <SidebarLink to="/about" active>
            About (active)
          </SidebarLink>
          <SidebarLink to="/users">Users</SidebarLink>
        </Stack>
      </Section>

      <Section
        title="Collapsed mode (icon rail)"
        hint="Pass collapsed / defaultCollapsed — sidebar shrinks to icon-rail width; hover to expand"
      >
        <Stack gap={3}>
          <Text>
            When <code>collapsed</code> is true, the sidebar shrinks to ~3rem wide and hides section
            + link labels; section + link icons remain. Hovering or focusing the sidebar temporarily
            expands it to full width (pure CSS — does not mutate state). The persistent collapsed
            state only flips when the user clicks the toggle button.
          </Text>
          <div
            style={{
              border: '1px solid var(--osd-color-border)',
              borderRadius: 'var(--osd-radius-md)',
              maxWidth: '20rem',
              overflow: 'hidden',
            }}
          >
            <Sidebar
              sections={SAMPLE_SECTIONS}
              ariaLabel="Docs example (collapsed)"
              defaultCollapsed
            />
          </div>
          <Text muted>
            Hover or focus the sidebar above to expand it. The chevron at the top toggles the
            persistent state; the localStorage key is <code>osd.nav.sidebar.collapsed</code>.
          </Text>
        </Stack>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Wrap-style"
            code={`import { Sidebar, type NavSection } from '@osd/ui/nav';
import { Home } from '@osd/icons/home';
import { Users } from '@osd/icons/users';

const SECTIONS: ReadonlyArray<NavSection> = [
  { id: 'main', label: 'Main', icon: <Home />, children: [
    { to: '/', label: 'Home', icon: <Home /> },
    { to: '/users', label: 'Users', icon: <Users /> },
  ] },
  { id: 'admin', label: 'Admin', adminOnly: true, children: [
    { to: '/admin/users', label: 'Users' },
  ] },
];

<Sidebar sections={SECTIONS} />
`}
          />

          <CodeBlock
            language="tsx"
            title="Collapsed (icon rail) — uncontrolled + persisted"
            code={`<Sidebar sections={SECTIONS} defaultCollapsed />
// persisted to localStorage under 'osd.nav.sidebar.collapsed'`}
          />

          <CodeBlock
            language="tsx"
            title="Collapsed — controlled"
            code={`const [collapsed, setCollapsed] = useState(false);
<Sidebar
  sections={SECTIONS}
  collapsed={collapsed}
  onCollapsedChange={setCollapsed}
/>`}
          />

          <CodeBlock
            language="tsx"
            title="With a router (TanStack Router example)"
            code={`// apps/web's SidebarLink is a thin wrapper around TanStack
// Router's <Link> that re-uses the BEM classes from @osd/ui:
//   className="osd-sidebar-link" + "osd-sidebar-link--active"
// on active.

import { Link } from '@tanstack/react-router';

<Link
  to={to}
  className="osd-sidebar-link"
  activeProps={{ className: 'osd-sidebar-link osd-sidebar-link--active' }}
  activeOptions={{ exact: to === '/' }}
>
  {label}
</Link>
`}
          />

          <CodeBlock
            language="tsx"
            title="Filtering sections by role"
            code={`import { useNavSections } from './features/nav/useNavSections';
import { Sidebar } from '@osd/ui/nav';

const isAdmin = useAuth().user?.isAdmin === true;
const sections = useNavSections(isAdmin);

<Sidebar sections={sections} />
`}
          />
        </Stack>
      </Section>

      <Section
        title="AppShell integration"
        hint="How the production web app wires Sidebar to TanStack Router. The router-aware SidebarLink replaces the design-system <a href> when the consumer needs client-side navigation."
      >
        <Stack gap={4}>
          <Text>
            The <code>SidebarSection</code> primitive renders a plain <code>&lt;a href&gt;</code>{' '}
            per link — that's deliberate so the design system stays router-agnostic. To use TanStack
            Router's <code>&lt;Link&gt;</code>, the production app re-implements the per-link
            surface with the router-aware wrapper at{' '}
            <code>apps/web/src/components/AppShell/SidebarLink.tsx</code>, re-using the{' '}
            <code>osd-sidebar-link</code> BEM classes:
          </Text>
          <div
            style={{
              border: '1px solid var(--osd-color-border)',
              borderRadius: 'var(--osd-radius-md)',
              maxWidth: '20rem',
              overflow: 'hidden',
              padding: '0.5rem',
            }}
          >
            <Stack gap={2}>
              <Text muted>
                Live router-aware links (click to navigate — current page highlights):
              </Text>
              <Stack gap={1}>
                <RouterSidebarLink to="/ui/sidebar" label="Sidebar docs (active here)" />
                <RouterSidebarLink to="/" label="Home" />
                <RouterSidebarLink to="/about" label="About" />
                <RouterSidebarLink to="/users" label="Users" />
                <RouterSidebarLink to="/ui/tabs" label="Tabs docs" />
              </Stack>
            </Stack>
          </div>
          <CodeBlock
            language="tsx"
            title="AppShell wiring (apps/web/src/components/AppShell/AppShell.tsx)"
            code={`import { Sidebar } from '@osd/ui/nav';
import { useNavSections } from '../../features/nav/useNavSections';

const sections = useNavSections(auth.user?.isAdmin === true);

<div className="osd-app-shell__body">
  <Sidebar sections={sections} />
  <main className="osd-app-shell__main">{children}</main>
  <MobileDrawer sections={sections} … />
</div>`}
          />
          <Text muted>
            The same <code>&lt;Sidebar&gt;</code> JSX is reused inside <code>MobileDrawer</code>{' '}
            (rendered below the <code>md</code> breakpoint) so the mobile experience mirrors the
            desktop one without any duplication of the section data.
          </Text>
        </Stack>
      </Section>

      <Section
        title="Mobile responsive"
        hint="The Sidebar layout is reused inside MobileDrawer; the 280 px width is the mobile breakpoint."
      >
        <div
          style={{
            border: '1px solid var(--osd-color-border)',
            borderRadius: 'var(--osd-radius-md)',
            width: '280px',
            overflow: 'hidden',
          }}
        >
          <Sidebar sections={SAMPLE_SECTIONS} ariaLabel="Mobile drawer example" />
        </div>
        <Text muted>
          The <code>MobileDrawer</code> component wraps <code>Sidebar</code> in a portal-based
          slide-out panel with backdrop, focus trap, and Escape-to-close — see{' '}
          <code>apps/web/src/components/AppShell/MobileDrawer.tsx</code>.
        </Text>
      </Section>

      <Section
        title="Accessibility"
        hint="Per-primitive a11y guarantees. The design system relies on native HTML where it can."
      >
        <Table columns="1fr 1.5fr 2.5fr">
          <Table.Header>
            <Table.Row>
              <Table.Head>Piece</Table.Head>
              <Table.Head>Guarantee</Table.Head>
              <Table.Head>Notes</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>
                <code>Sidebar</code>
              </Table.Cell>
              <Table.Cell>
                Renders as <code>&lt;nav aria-label&gt;</code>
              </Table.Cell>
              <Table.Cell>
                <code>ariaLabel</code> prop defaults to <code>"Primary"</code>; pass an explicit
                label when nesting multiple nav surfaces.
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Collapse toggle</Table.Cell>
              <Table.Cell>
                <code>aria-expanded</code> + state-dependent <code>aria-label</code>
              </Table.Cell>
              <Table.Cell>
                <code>aria-expanded="false"</code> when collapsed, <code>"true"</code> when
                expanded. <code>aria-label</code> flips between <code>"Collapse sidebar"</code> and{' '}
                <code>"Expand sidebar"</code> so screen-reader users hear the current state.
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Hover-to-expand</Table.Cell>
              <Table.Cell>
                <code>:hover</code> + <code>:focus-within</code> in CSS only
              </Table.Cell>
              <Table.Cell>
                Transient expand does not fire <code>onCollapsedChange</code>; keyboard users get
                the same expand-on-focus behaviour automatically via <code>:focus-within</code>.
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <code>SidebarSection</code>
              </Table.Cell>
              <Table.Cell>
                Native <code>&lt;details&gt;</code> + <code>&lt;summary&gt;</code>
              </Table.Cell>
              <Table.Cell>
                Keyboard open/close and screen-reader announcement come from the platform; no ARIA
                needed. Open state persists to <code>localStorage</code> under{' '}
                <code>osd.nav.&lt;id&gt;.open</code>. In collapsed mode the summary's label is
                visually hidden (CSS) but still announced to screen readers.
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <code>SidebarLink</code>
              </Table.Cell>
              <Table.Cell>
                <code>aria-current="page"</code> when active
              </Table.Cell>
              <Table.Cell>
                Pass <code>active</code> to apply the BEM modifier AND the ARIA attribute.
                Router-aware consumers should derive <code>active</code> from the router rather than
                passing it by hand.
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>localStorage persistence</Table.Cell>
              <Table.Cell>SSR-safe</Table.Cell>
              <Table.Cell>
                All reads are guarded by <code>typeof window === 'undefined'</code>; the server
                render always uses the default-open state (Main / admin / design open; everything
                else closed). First client render matches that, then hydrates. Same pattern for the
                sidebar-level collapsed state under <code>osd.nav.sidebar.collapsed</code>.
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </Section>
    </Stack>
  );
}
